import {
  Component,
  ErrorHandler,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  AfterViewInit,
  HostListener,
} from '@angular/core';

@Component({
  selector: 'app-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
})
export class TabsComponent
  implements OnInit, OnChanges, OnDestroy, ErrorHandler, AfterViewInit
{
  @Input() tabs: Tab[] = [];
  @Input() currentTab?: number;
  @Input() dashboardTab: boolean = false;
  @Output() onChangeTab = new EventEmitter<number>();

  @ViewChild('tabsList', { static: false }) tabsList!: ElementRef;
  @ViewChildren('tabItem') tabItems!: QueryList<ElementRef<HTMLElement>>;

  activedTab: number = 0;
  posX = '0px';
  width = '0px';

  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;
  showArrows: boolean = false;

  private indicatorFrame?: number;
  private tabItemsChangesSub?: { unsubscribe(): void };

  constructor() {}

  handleError(error: any): void {
    console.table(error);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentTab'] && this.currentTab != null) {
      this.activedTab = this.currentTab;
      this.setIndicator();
    }

    if (changes['tabs']) {
      this.setIndicator();
    }
  }

  ngOnInit(): void {
    if (this.currentTab != null) {
      this.activedTab = this.currentTab;
    }
  }

  ngAfterViewInit(): void {
    this.setIndicator();
    this.checkScrollState();
    this.tabItemsChangesSub = this.tabItems.changes.subscribe(() =>
      this.setIndicator()
    );
  }

  ngOnDestroy(): void {
    this.tabItemsChangesSub?.unsubscribe();
    if (this.indicatorFrame != null) {
      cancelAnimationFrame(this.indicatorFrame);
    }
  }

  @HostListener('window:resize')
  onResize(): void {
    this.checkScrollState();
  }

  checkScrollState(): void {
    setTimeout(() => {
      if (this.tabsList) {
        const element = this.tabsList.nativeElement;
        this.canScrollLeft = element.scrollLeft > 0;
        this.canScrollRight =
          element.scrollLeft < element.scrollWidth - element.clientWidth;
        this.showArrows = element.scrollWidth > element.clientWidth;
      }
    }, 100);
  }

  scrollLeft(): void {
    if (this.tabsList && this.canScrollLeft) {
      const element = this.tabsList.nativeElement;
      element.scrollBy({ left: -400, behavior: 'smooth' });
      setTimeout(() => this.checkScrollState(), 300);
    }
  }

  scrollRight(): void {
    if (this.tabsList && this.canScrollRight) {
      const element = this.tabsList.nativeElement;
      element.scrollBy({ left: 400, behavior: 'smooth' });
      setTimeout(() => this.checkScrollState(), 300);
    }
  }

  onScroll(): void {
    this.checkScrollState();
  }

  setIndicator(): void {
    if (this.indicatorFrame != null) {
      cancelAnimationFrame(this.indicatorFrame);
    }

    this.indicatorFrame = requestAnimationFrame(() => {
      const elements = this.tabItems?.toArray() ?? [];
      const activeEl = elements[this.activedTab]?.nativeElement;

      if (activeEl) {
        this.width = `${activeEl.offsetWidth}px`;
        this.posX = `${activeEl.offsetLeft}px`;
      }

      this.checkScrollState();
      this.indicatorFrame = undefined;
    });
  }

  activeTab(index: number, event: Event): void {
    if (this.tabs[index].disabled) {
      return;
    }

    const element = (event.currentTarget ||
      event.target) as HTMLElement | null;

    if (element) {
      this.width = `${element.offsetWidth}px`;
      this.posX = `${element.offsetLeft}px`;
    }

    this.activedTab = index;
    this.onChangeTab.emit(index);
  }
}
